/**
 * PreProduct Session Handler
 *
 * This script saves existing cart items before PreProduct redirects to checkout,
 * then restores them when the user returns to the site.
 *
 * Flow:
 * 1. User has items in cart
 * 2. User clicks pre-order button
 * 3. We save their current cart items
 * 4. PreProduct redirects to checkout with only pre-order product
 * 5. User returns to site
 * 6. We restore their original cart items and show a reminder modal
 *
 * Documentation: https://preproduct.io/docs/pre-order-javascript-event/
 */

(function () {
  "use strict";

  const STORAGE_KEY = "preproduct_saved_cart";
  const RESTORATION_FLAG = "preproduct_restoring";
  const PREORDER_BUTTON_TEXT = "pre-order";

  /**
   * Get current cart contents
   * @returns {Promise<Object>} Current cart data
   */
  async function getCurrentCart() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch cart");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching current cart:", error);
      return null;
    }
  }

  /**
   * Save current cart items to session storage
   * @param {Object} cartData - Current cart data from /cart.js
   */
  function saveCartToSession(cartData) {
    try {
      if (!cartData || !cartData.items || cartData.items.length === 0) {
        return false;
      }

      const itemsData = {
        timestamp: Date.now(),
        items: cartData.items.map((item) => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {},
          selling_plan: item.selling_plan_allocation?.selling_plan?.id || null,
        })),
        itemCount: cartData.item_count,
      };

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(itemsData));
      console.log("Cart items saved to session:", itemsData);
      return true;
    } catch (error) {
      console.error("Error saving cart items to session:", error);
      return false;
    }
  }

  /**
   * Get saved items from session storage
   * @returns {Object|null} The saved items data or null if not found
   */
  function getSavedItems() {
    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error retrieving PreProduct items from session:", error);
      return null;
    }
  }

  /**
   * Clear saved items from session storage
   */
  function clearSavedItems() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing PreProduct session items:", error);
    }
  }

  /**
   * Check if we're currently restoring items (to prevent loops)
   */
  function isRestoring() {
    return sessionStorage.getItem(RESTORATION_FLAG) === "true";
  }

  /**
   * Set restoration flag
   */
  function setRestorationFlag(value) {
    if (value) {
      sessionStorage.setItem(RESTORATION_FLAG, "true");
    } else {
      sessionStorage.removeItem(RESTORATION_FLAG);
    }
  }

  /**
   * Clear the cart
   * @returns {Promise} Promise that resolves when cart is cleared
   */
  async function clearCart() {
    try {
      const response = await fetch("/cart/clear.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to clear cart");
      }

      return true;
    } catch (error) {
      console.error("Error clearing cart:", error);
      return false;
    }
  }

  /**
   * Add items to cart
   * @param {Array} items - Array of items to add (from PreProduct event)
   * @returns {Promise} Promise that resolves with the cart response including sections
   */
  async function addItemsToCart(items) {
    try {
      // Build payload with all items at once for better performance
      const payload = {
        items: items.map((item) => {
          const cartItem = {
            id: item.id,
            quantity: parseInt(item.quantity) || 1,
            properties: item.properties || {},
          };

          // Only add selling_plan if it exists
          if (item.selling_plan) {
            cartItem.selling_plan = item.selling_plan;
          }

          return cartItem;
        }),
        // Request sections to update cart counter
        sections: "mini-cart,header",
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Failed to add items to cart:", errorData);
        throw new Error(errorData.description || "Failed to add items to cart");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error adding items to cart:", error);
      throw error;
    }
  }

  /**
   * Restore saved items to cart
   */
  async function restoreItems() {
    // Prevent restoration loops
    if (isRestoring()) {
      return;
    }

    const savedData = getSavedItems();

    if (!savedData || !savedData.items || savedData.items.length === 0) {
      console.log("No PreProduct items to restore");
      return;
    }

    // Set restoration flag to prevent loops
    setRestorationFlag(true);

    try {
      // Clear existing cart
      await clearCart();

      // Add saved items back to cart (now includes sections for cart counter update)
      const cartResponse = await addItemsToCart(savedData.items);

      // Update cart UI sections (header counter, mini-cart)
      if (cartResponse && cartResponse.sections) {
        // Use the theme's section re-rendering utility if available
        if (typeof window.HH?.utils?.reRenderShopifySections === "function") {
          window.HH.utils.reRenderShopifySections(cartResponse);
        } else {
          // Fallback: manually update sections and dispatch event
          updateCartSections(cartResponse.sections);
        }
      }

      // Optionally refresh the cart UI
      if (typeof window.refreshCart === "function") {
        window.refreshCart();
      }

      // Dispatch cart update event for theme to hook into
      document.dispatchEvent(
        new CustomEvent("CART:TEMPLATE_UPDATE", {
          bubbles: true,
          composed: true,
        })
      );

      // Trigger a custom event for other scripts to hook into
      window.dispatchEvent(
        new CustomEvent("preproductCartRestored", {
          detail: savedData,
        })
      );

      // Show reminder modal
      showCartReminderModal(savedData);

      // Clear saved items after successful restoration and modal display
      clearSavedItems();
    } catch (error) {
      console.error("Error restoring cart items:", error);
      // Don't clear saved items on error, so user can try again
    } finally {
      // Clear restoration flag
      setRestorationFlag(false);
    }
  }

  /**
   * Update cart sections in the DOM
   * @param {Object} sections - Sections object from cart API response
   */
  function updateCartSections(sections) {
    Object.keys(sections).forEach((key) => {
      const updatedHTML = sections[key];
      const parser = new DOMParser();
      const doc = parser.parseFromString(updatedHTML, "text/html");
      const updatedNodes = doc.querySelectorAll("[data-rerender]");

      // Find the corresponding section in the current document
      const sectionElement = document.querySelector(
        `[id^="shopify-section-"][id*="${key}"]`
      );

      if (sectionElement) {
        const currentNodes = sectionElement.querySelectorAll("[data-rerender]");
        currentNodes.forEach((node, index) => {
          if (updatedNodes[index]) {
            node.replaceWith(updatedNodes[index]);
          }
        });
      }
    });
  }

  /**
   * Show modal to remind user about their cart items
   * @param {Object} restoredData - The restored cart data
   */
  function showCartReminderModal(restoredData) {
    // Get modal settings from global variables (set in Liquid)
    const modalSettings = window.preproductModal || {};
    const headline = modalSettings.headline || "Don't wait!";
    const subheading =
      modalSettings.subheading || "Ready to complete your order?";
    const descriptionTemplate =
      modalSettings.description ||
      "You have {count} waiting in your cart. Review your items and proceed to checkout.";
    const buttonText = modalSettings.buttonText || "CHECK OUT NOW";
    const continueText = modalSettings.continueText || "CONTINUE SHOPPING";
    const imageUrl = modalSettings.imageUrl || "";

    // Create item count text
    const itemText = restoredData.itemCount === 1 ? "item" : "items";
    const itemCountText = `${restoredData.itemCount} ${itemText}`;

    // Replace {count} placeholder in description
    const description = descriptionTemplate.replace(
      "{count}",
      `<strong>${itemCountText}</strong>`
    );

    // Create image HTML
    const imageHTML = imageUrl
      ? `<img src="${imageUrl}" alt="Products" style="width: 100%; height: 100%; object-fit: cover;">`
      : `<div class="preproduct-modal__placeholder">PRODUCT<br>IMAGE<br>PLACEHOLDER</div>`;

    const modalHTML = `
      <div 
        id="preproduct-cart-reminder-modal" 
        class="preproduct-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="preproduct-modal-headline"
        aria-describedby="preproduct-modal-description"
      >
        <div class="preproduct-modal__container">
          <button 
            id="preproduct-close-modal-btn" 
            class="preproduct-modal__close"
            aria-label="Close cart reminder modal"
            type="button"
          >
            ✕
          </button>
          
          <!-- Left side: Content -->
          <div class="preproduct-modal__content">
            <h2 
              id="preproduct-modal-headline"
              class="preproduct-modal__headline"
            >
              ${headline}
            </h2>
            
            <p class="preproduct-modal__subheading">
              ${subheading}
            </p>
            
            <p 
              id="preproduct-modal-description"
              class="preproduct-modal__description"
            >
              ${description}
            </p>

            <button 
              id="preproduct-view-cart-btn" 
              class="preproduct-modal__button"
              type="button"
            >
              ${buttonText}
            </button>
            <button 
              id="preproduct-continue-shopping-btn" 
              class="preproduct-modal__button--link"
              type="button"
            >
              ${continueText}
            </button>
          </div>
          
          <!-- Right side: Product image -->
          <div class="preproduct-modal__image" aria-hidden="true">
            ${imageHTML}
            
            <!-- Optional: Add your brand logo or text overlay here -->
            <div class="preproduct-modal__brand">
              <!-- BRAND LOGO HERE -->
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert modal into DOM
    const modalContainer = document.createElement("div");
    modalContainer.innerHTML = modalHTML;
    document.body.appendChild(modalContainer.firstElementChild);

    // Get modal and focusable elements
    const modal = document.getElementById("preproduct-cart-reminder-modal");
    const viewCartBtn = document.getElementById("preproduct-view-cart-btn");
    const continueShoppingBtn = document.getElementById(
      "preproduct-continue-shopping-btn"
    );
    const closeModalBtn = document.getElementById("preproduct-close-modal-btn");

    // Store the previously focused element to restore focus when modal closes
    const previouslyFocusedElement = document.activeElement;

    // Get all focusable elements within the modal for focus trapping
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];

    // Function to close modal and cleanup
    function closeModal() {
      modal.remove();
      // Restore focus to the previously focused element
      if (previouslyFocusedElement && previouslyFocusedElement.focus) {
        previouslyFocusedElement.focus();
      }
      // Remove keyboard event listener
      document.removeEventListener("keydown", keyboardHandler);
    }

    // Keyboard event handler for ESC and focus trap
    function keyboardHandler(e) {
      // Close on ESC key
      if (e.key === "Escape") {
        closeModal();
        return;
      }

      // Focus trap: Tab key navigation
      if (e.key === "Tab") {
        if (e.shiftKey) {
          // Shift + Tab: moving backwards
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault();
            lastFocusableElement.focus();
          }
        } else {
          // Tab: moving forwards
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            firstFocusableElement.focus();
          }
        }
      }
    }

    // Button click handlers
    viewCartBtn.addEventListener("click", () => {
      window.location.href = "/cart";
    });

    continueShoppingBtn.addEventListener("click", () => {
      closeModal();
    });

    closeModalBtn.addEventListener("click", () => {
      closeModal();
    });

    // Close on backdrop click
    modal.addEventListener("click", (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });

    // Add keyboard event listener
    document.addEventListener("keydown", keyboardHandler);

    // Set initial focus to the primary action button
    // Use setTimeout to ensure modal is fully rendered
    setTimeout(() => {
      viewCartBtn.focus();
    }, 100);
  }

  /**
   * Set button loading state
   * @param {HTMLElement} button - The button element
   * @param {boolean} isLoading - Whether to show loading state
   */
  function setButtonLoadingState(button, isLoading) {
    if (!button) {
      console.warn("setButtonLoadingState: No button provided");
      return;
    }

    if (isLoading) {
      // Store original text if not already stored
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent.trim();
      }

      // Store original HTML if button has child elements
      if (!button.dataset.originalHtml && button.children.length > 0) {
        button.dataset.originalHtml = button.innerHTML;
      }

      // Set loading state
      button.disabled = true;

      // If button has child elements (like spans), update innerHTML
      // Otherwise update textContent
      if (button.children.length > 0) {
        button.innerHTML = "Adding to cart...";
      } else {
        button.textContent = "Adding to cart...";
      }

      button.classList.add("button--loading");
    } else {
      // Restore original state
      if (button.dataset.originalHtml) {
        button.innerHTML = button.dataset.originalHtml;
        delete button.dataset.originalHtml;
      } else if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
        delete button.dataset.originalText;
      }

      button.disabled = false;
      button.classList.remove("button--loading");
    }
  }

  /**
   * Find all PreProduct buttons and try to determine which was clicked
   */
  function findPreProductButton(event) {
    // Try to get button from event detail
    if (event.detail?.button) {
      return event.detail.button;
    }

    // Try to get button from event target
    if (event.target?.hasAttribute?.("data-native-pre-order-btn")) {
      return event.target;
    }

    // Look for all preorder buttons
    const allButtons = document.querySelectorAll("[data-native-pre-order-btn]");

    // Try to find a visible, non-disabled button
    for (const btn of allButtons) {
      const isVisible =
        btn.offsetParent !== null &&
        window.getComputedStyle(btn).display !== "none" &&
        window.getComputedStyle(btn).visibility !== "hidden";
      const isDisabled = btn.disabled || btn.hasAttribute("disabled");

      if (isVisible && !isDisabled) {
        return btn;
      }
    }

    // Fallback: return first button found
    if (allButtons.length > 0) {
      return allButtons[0];
    }

    console.warn("No PreProduct button found");
    return null;
  }

  /**
   * Handle PreProduct button click event
   * This fires BEFORE the conversion, so we can save the current cart
   */
  async function handleButtonClick(event) {
    // Find the clicked button
    const button = findPreProductButton(event);

    if (!button) {
      console.warn("Could not find PreProduct button to update");
    }

    // Set button to loading state
    if (button) {
      setButtonLoadingState(button, true);

      // Store button reference for potential error recovery
      window.preproductActiveButton = button;

      // Clear any existing timeout
      if (window.preproductButtonTimeout) {
        clearTimeout(window.preproductButtonTimeout);
      }

      // Set a safety timeout to revert button state if process takes too long
      // This prevents the button from getting stuck in loading state
      window.preproductButtonTimeout = setTimeout(() => {
        console.warn("PreProduct operation timed out, reverting button state");
        if (window.preproductActiveButton) {
          setButtonLoadingState(window.preproductActiveButton, false);
          window.preproductActiveButton = null;
        }
      }, 15000); // 15 second timeout
    }

    try {
      // Get current cart contents
      const cartData = await getCurrentCart();

      if (cartData && cartData.items && cartData.items.length > 0) {
        // Save current cart items to session
        saveCartToSession(cartData);
      } else {
        console.log("No items in cart to save");
      }
    } catch (error) {
      console.error("Error saving cart before PreProduct checkout:", error);

      // Revert button state on error
      if (button) {
        setButtonLoadingState(button, false);

        // Clear timeout
        if (window.preproductButtonTimeout) {
          clearTimeout(window.preproductButtonTimeout);
          window.preproductButtonTimeout = null;
        }
      }
    }

    // Let PreProduct continue its normal flow
  }

  /**
   * Handle PreProduct conversion error
   * This fires if the conversion fails
   */
  function handleConversionError(event) {
    console.error("PreProduct conversion error:", event);

    // Clear timeout
    if (window.preproductButtonTimeout) {
      clearTimeout(window.preproductButtonTimeout);
      window.preproductButtonTimeout = null;
    }

    // Restore button state if we have a reference to it
    const button = window.preproductActiveButton;
    if (button) {
      setButtonLoadingState(button, false);
      window.preproductActiveButton = null;
    }
  }

  /**
   * Handle successful PreProduct conversion
   * This fires when the conversion succeeds (before redirect)
   */
  function handleConversionSuccess(event) {
    // Clear timeout
    if (window.preproductButtonTimeout) {
      clearTimeout(window.preproductButtonTimeout);
      window.preproductButtonTimeout = null;
    }

    // Button will be removed from page during redirect, so no need to reset
    // Just clean up the reference
    window.preproductActiveButton = null;
  }

  /**
   * Setup direct click listeners on PreProduct buttons
   * This is a backup in case the PreProduct event doesn't fire
   * Also handles saving cart for product cards in collections/search
   */
  function setupDirectClickListeners() {
    // Use event delegation on the document for dynamically added buttons
    document.addEventListener(
      "click",
      async (e) => {
        const button = e.target.closest("[data-native-pre-order-btn]");
        const isPreorderButton = button?.textContent?.toLowerCase()?.indexOf(
          PREORDER_BUTTON_TEXT.toLowerCase()
        ) !== -1;

        if (button && !button.disabled && isPreorderButton) {
          console.log(
            "PreProduct button clicked - saving cart and setting loading state"
          );

          // Set button to loading state immediately
          setButtonLoadingState(button, true);
          window.preproductActiveButton = button;

          // Clear any existing timeout
          if (window.preproductButtonTimeout) {
            clearTimeout(window.preproductButtonTimeout);
          }

          // Set safety timeout
          window.preproductButtonTimeout = setTimeout(() => {
            console.warn("PreProduct operation timed out (from direct click)");
            if (window.preproductActiveButton) {
              setButtonLoadingState(window.preproductActiveButton, false);
              window.preproductActiveButton = null;
            }
          }, 15000);

          // Save the current cart before PreProduct processes
          try {
            const cartData = await getCurrentCart();

            if (cartData && cartData.items && cartData.items.length > 0) {
              const saved = saveCartToSession(cartData);
              if (saved) {
                console.log("Cart saved successfully from direct click");
              }
            } else {
              console.log("No items in cart to save");
            }
          } catch (error) {
            console.error("Error saving cart from direct click:", error);
          }
        }
      },
      true
    ); // Use capture phase to intercept early
  }

  /**
   * Initialize the handler
   */
  function init() {
    // Prevent multiple initializations
    if (window.preproductSessionInitialized) {
      console.warn("PreProduct Session Handler already initialized");
      return;
    }
    window.preproductSessionInitialized = true;

    // Setup direct click listeners as a backup
    setupDirectClickListeners();

    // Listen for button click events (fires BEFORE checkout redirect)
    window.addEventListener(
      "ppConvertingButtonClicked",
      handleButtonClick,
      false
    );

    // Listen for conversion success event
    window.addEventListener(
      "ppConversionSuccess",
      handleConversionSuccess,
      false
    );

    // Listen for conversion error event
    window.addEventListener("ppConversionError", handleConversionError, false);

    // Also listen for generic errors that might occur
    window.addEventListener("ppConversionFailed", handleConversionError, false);

    // On page load, check if we need to restore items
    // Use a flag to ensure restoration only happens once
    let restorationScheduled = false;

    const scheduleRestoration = () => {
      if (!restorationScheduled) {
        restorationScheduled = true;
        restoreItems(); // Immediate execution
      }
    };

    // Reduced delay for faster modal appearance
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", scheduleRestoration, {
        once: true,
      });
    } else {
      // Document already loaded/interactive
      scheduleRestoration();
    }

    // Expose utility functions to window for debugging
    window.preproductSessionHandler = {
      getSavedItems,
      clearSavedItems,
      restoreItems,
      isRestoring,
      getCurrentCart,
      setButtonLoadingState,
      findPreProductButton,
    };
  }

  // Initialize when script loads
  init();
})();
